import * as fs from "fs/promises";
import * as fsSync from "fs";
import * as path from "path";
import type { StorageFile, StorageReadDisposition } from "@gencow/core";
import { recordStorageImageTransform, recordStorageImageTransformSize } from "./storage-metering.js";
import { decideStorageRouteReadAccess, type StorageRouteAccessOptions } from "./storage-route-access.js";
import type {
  StorageImageTierConfig,
  StorageMeteringOptions,
  StorageRuntimeAdapter,
} from "./storage-types.js";
import {
  normalizeStorageVisibility,
  resolveStorageDisposition,
  storageCacheControl,
  type StorageCacheScope,
} from "./storage-visibility.js";

interface ImageTransformParams {
  w?: number;
  h?: number;
  f?: string;
  q?: number;
  fit?: string;
}

const TRANSFORMABLE_TYPES = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"]);
const ALLOWED_FORMATS = new Set(["webp", "avif", "jpeg", "png"]);
const ALLOWED_FIT_MODES = new Set(["cover", "contain", "fill", "inside"]);
const MAX_DIMENSION = 4096;
const DEFAULT_QUALITY = 80;
const AUTO_WEBP_QUALITY = 75;
const AUTO_WEBP_EFFORT = 6;
const MAX_CACHE_ENTRIES = 1000;
const baseContentTypes: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  avif: "image/avif",
};

let activeTransforms = 0;
const MAX_CONCURRENT_TRANSFORMS = 3;
const transformQueue: Array<() => void> = [];

function acquireTransformSlot(): Promise<void> {
  if (activeTransforms < MAX_CONCURRENT_TRANSFORMS) {
    activeTransforms++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    transformQueue.push(() => {
      activeTransforms++;
      resolve();
    });
  });
}

function releaseTransformSlot(): void {
  activeTransforms--;
  const next = transformQueue.shift();
  if (next) next();
}

function parseTransformParams(
  wStr?: string,
  hStr?: string,
  fStr?: string,
  qStr?: string,
  fitStr?: string,
): ImageTransformParams | null {
  const params: ImageTransformParams = {};
  let hasParam = false;

  if (wStr) {
    const w = parseInt(wStr, 10);
    if (isNaN(w) || w < 1 || w > MAX_DIMENSION) return null;
    params.w = w;
    hasParam = true;
  }
  if (hStr) {
    const h = parseInt(hStr, 10);
    if (isNaN(h) || h < 1 || h > MAX_DIMENSION) return null;
    params.h = h;
    hasParam = true;
  }
  if (fStr) {
    const fmt = fStr.toLowerCase();
    if (!ALLOWED_FORMATS.has(fmt)) return null;
    params.f = fmt;
    hasParam = true;
  }
  if (qStr) {
    const q = parseInt(qStr, 10);
    if (isNaN(q) || q < 1 || q > 100) return null;
    params.q = q;
    hasParam = true;
  }
  if (fitStr) {
    const fitMode = fitStr.toLowerCase();
    if (!ALLOWED_FIT_MODES.has(fitMode)) return null;
    params.fit = fitMode;
    hasParam = true;
  }
  return hasParam ? params : null;
}

function buildCacheKey(
  storageId: string,
  params: ImageTransformParams,
  isAutoWebp: boolean,
  autoMaxWidth?: number,
  autoQuality?: number,
): string {
  if (isAutoWebp) {
    const mwSuffix = autoMaxWidth ? `_mw${autoMaxWidth}` : "";
    const qSuffix = autoQuality && autoQuality !== AUTO_WEBP_QUALITY ? `_q${autoQuality}` : "";
    return `${storageId}_auto_webp${mwSuffix}${qSuffix}.webp`;
  }
  const parts: string[] = [storageId];
  if (params.w) parts.push(`w${params.w}`);
  if (params.h) parts.push(`h${params.h}`);
  const fmt = params.f || "webp";
  parts.push(`f${fmt}`);
  parts.push(`q${params.q || DEFAULT_QUALITY}`);
  if (params.fit) parts.push(params.fit);
  return `${parts.join("_")}.${fmt === "jpeg" ? "jpg" : fmt}`;
}

async function getCacheEntryCount(cacheDir: string): Promise<number> {
  try {
    const entries = await fs.readdir(cacheDir);
    return entries.length;
  } catch {
    return 0;
  }
}

function resolveStoredPath(meta: StorageFile, storageDir: string): string {
  const pathValue = (meta as StorageFile & { path?: unknown }).path;
  return typeof pathValue === "string" ? pathValue : path.join(storageDir, meta.id);
}

export function storageRoutes(
  storage: StorageRuntimeAdapter,
  rawSql?: (sql: string, params?: unknown[]) => Promise<unknown[]>,
  storageDir?: string,
  tierConfig?: StorageImageTierConfig,
  meteringOptions?: StorageMeteringOptions,
  accessOptions?: StorageRouteAccessOptions,
) {
  const baseTierConfig: Required<StorageImageTierConfig> = {
    autoWebp: tierConfig?.autoWebp ?? true,
    autoMaxWidth: tierConfig?.autoMaxWidth ?? 0,
    resize: tierConfig?.resize ?? true,
    formats: tierConfig?.formats ?? ["webp", "avif", "jpeg", "png"],
    qualityControl: tierConfig?.qualityControl ?? true,
    cacheMaxMB: tierConfig?.cacheMaxMB ?? 1024,
    transformsPerMonth: tierConfig?.transformsPerMonth ?? -1,
    autoQuality: tierConfig?.autoQuality ?? AUTO_WEBP_QUALITY,
  };

  function getImageConfig(uploadsDir: string): Required<StorageImageTierConfig> {
    const config = { ...baseTierConfig };
    try {
      const raw = fsSync.readFileSync(path.join(uploadsDir, ".image-config.json"), "utf-8");
      const appConfig = JSON.parse(raw);
      if (appConfig.autoMaxWidth > 0) {
        config.autoMaxWidth =
          config.autoMaxWidth > 0
            ? Math.min(appConfig.autoMaxWidth, config.autoMaxWidth)
            : appConfig.autoMaxWidth;
      }
      if (appConfig.autoQuality > 0 && appConfig.autoQuality <= 100) {
        config.autoQuality = appConfig.autoQuality;
      }
      if (appConfig.autoWebp === false) {
        config.autoWebp = false;
      }
    } catch {
      /* 파일 없음 or 파싱 실패 → Tier 기본값 유지 */
    }
    return config;
  }

  let sharpModule: any = null;
  let sharpLoadAttempted = false;

  async function getSharp(): Promise<any> {
    if (sharpLoadAttempted) return sharpModule;
    sharpLoadAttempted = true;
    try {
      // @ts-ignore — sharp는 런타임 선택적 의존성 (설치 안 된 환경에서도 빌드 가능)
      sharpModule = (await import("sharp")).default;
    } catch {
      console.warn("[storage] sharp not available — image optimization disabled");
      sharpModule = null;
    }
    return sharpModule;
  }

  return async (c: {
    req: {
      param: (key: string) => string;
      query: (key: string) => string | undefined;
      header: (name: string) => string | undefined;
    };
    json: (data: unknown, status?: number) => Response;
    body: (data: unknown, status: number, headers: Record<string, string>) => Response;
    get?: (key: string) => unknown;
  }) => {
    const dir = storageDir || "./uploads";
    const config = getImageConfig(dir);
    const id = c.req.param("id");
    let meta = await storage.getMeta(id);

    if (!meta && rawSql) {
      try {
        const rows = await rawSql(
          `SELECT storage_id, name, size, type, visibility FROM _system_files WHERE storage_id = $1 LIMIT 1`,
          [id],
        );
        if (rows.length > 0) {
          const row = rows[0] as Record<string, string>;
          meta = {
            id: row.storage_id,
            name: row.name,
            size: Number(row.size),
            type: row.type || "application/octet-stream",
            visibility: normalizeStorageVisibility(row.visibility),
          };
        }
      } catch {
        try {
          const rows = await rawSql(
            `SELECT storage_id, name, size, type FROM _system_files WHERE storage_id = $1 LIMIT 1`,
            [id],
          );
          if (rows.length > 0) {
            const row = rows[0] as Record<string, string>;
            meta = {
              id: row.storage_id,
              name: row.name,
              size: Number(row.size),
              type: row.type || "application/octet-stream",
              visibility: "private",
            };
          }
        } catch {
          /* fallthrough to access decision */
        }
      }
    }

    const readAccess = decideStorageRouteReadAccess({
      storageId: id,
      meta,
      grant: c.req.query("grant"),
      identity: getRouteIdentity(c),
      accessOptions,
    });
    if (!readAccess.ok) return c.json({ error: readAccess.error }, readAccess.status);
    if (!meta) return c.json({ error: "Not found" }, 404);

    const isTransformable = TRANSFORMABLE_TYPES.has(meta.type);
    const transformParams = isTransformable
      ? parseTransformParams(
          c.req.query("w"),
          c.req.query("h"),
          c.req.query("f"),
          c.req.query("q"),
          c.req.query("fit"),
        )
      : null;

    const acceptHeader = c.req.header("accept") || "";
    const clientAcceptsWebp = acceptHeader.includes("image/webp");
    const isAutoWebp =
      !transformParams &&
      isTransformable &&
      clientAcceptsWebp &&
      config.autoWebp &&
      (meta.type === "image/png" || meta.type === "image/jpeg" || meta.type === "image/jpg");

    if (!transformParams && !isAutoWebp) {
      return serveOriginal(
        c,
        meta,
        dir,
        readAccess.disposition,
        readAccess.cacheScope,
        readAccess.cacheMaxAgeSeconds,
      );
    }

    if (transformParams) {
      if ((transformParams.w || transformParams.h) && !config.resize) {
        return c.json(
          {
            error: "Image resize requires Pro plan",
            code: "PLAN_LIMIT",
            upgrade: "https://gencow.app/pricing",
          },
          403,
        );
      }
      if (transformParams.f && !config.formats.includes(transformParams.f)) {
        return c.json(
          {
            error: `Format "${transformParams.f}" requires a higher plan`,
            code: "PLAN_LIMIT",
            upgrade: "https://gencow.app/pricing",
            allowed: { formats: config.formats },
          },
          403,
        );
      }
      if (transformParams.q && !config.qualityControl) {
        return c.json(
          {
            error: "Quality control requires Pro plan",
            code: "PLAN_LIMIT",
            upgrade: "https://gencow.app/pricing",
          },
          403,
        );
      }
      if (transformParams.fit && !config.resize) {
        return c.json(
          {
            error: "Fit mode requires Pro plan",
            code: "PLAN_LIMIT",
            upgrade: "https://gencow.app/pricing",
          },
          403,
        );
      }
    }

    const sharp = await getSharp();
    if (!sharp) {
      return serveOriginal(
        c,
        meta,
        dir,
        readAccess.disposition,
        readAccess.cacheScope,
        readAccess.cacheMaxAgeSeconds,
      );
    }

    const cacheKey = buildCacheKey(
      id,
      transformParams || {},
      isAutoWebp,
      config.autoMaxWidth || undefined,
      config.autoQuality,
    );

    if (storage.kind === "azure-blob") {
      const cached = await storage.getTransform?.(id, cacheKey);
      if (cached) {
        return serveTransformBuffer(
          c,
          cached.buffer,
          transformParams,
          isAutoWebp,
          meta,
          readAccess.disposition,
          readAccess.cacheScope,
          readAccess.cacheMaxAgeSeconds,
        );
      }
      if (storage.countTransforms && (await storage.countTransforms()) >= MAX_CACHE_ENTRIES) {
        return serveOriginal(
          c,
          meta,
          dir,
          readAccess.disposition,
          readAccess.cacheScope,
          readAccess.cacheMaxAgeSeconds,
        );
      }
      if (!storage.storeTransform) {
        return serveOriginal(
          c,
          meta,
          dir,
          readAccess.disposition,
          readAccess.cacheScope,
          readAccess.cacheMaxAgeSeconds,
        );
      }
      try {
        await acquireTransformSlot();
        try {
          const original = await storage.get(id);
          if (!original) return c.json({ error: "Not found" }, 404);
          let pipeline = sharp(original.buffer);

          if (transformParams?.w || transformParams?.h) {
            pipeline = pipeline.resize({
              width: transformParams.w || undefined,
              height: transformParams.h || undefined,
              fit: (transformParams.fit || "cover") as "cover" | "contain" | "fill" | "inside",
              withoutEnlargement: true,
            });
          } else if (isAutoWebp && config.autoMaxWidth > 0) {
            pipeline = pipeline.resize({ width: config.autoMaxWidth, withoutEnlargement: true });
          }

          const outputFormat = transformParams?.f || (isAutoWebp ? "webp" : null);
          const quality = transformParams?.q ?? DEFAULT_QUALITY;
          if (outputFormat === "webp") {
            pipeline = pipeline.webp(
              isAutoWebp
                ? { quality: config.autoQuality, effort: AUTO_WEBP_EFFORT, alphaQuality: 100 }
                : { quality, alphaQuality: 100 },
            );
          } else if (outputFormat === "avif") {
            pipeline = pipeline.avif({ quality });
          } else if (outputFormat === "jpeg" || outputFormat === "jpg") {
            pipeline = pipeline.jpeg({ quality, mozjpeg: true });
          } else if (outputFormat === "png") {
            pipeline = pipeline.png({ compressionLevel: 9 });
          }

          const transformed = await pipeline.toBuffer();
          const cacheStats = await recordStorageImageTransformSize(meteringOptions, transformed.byteLength, {
            sourceBytes: meta.size,
            format: outputFormat || meta.type.replace(/^image\//, ""),
            autoWebp: isAutoWebp,
          });
          if (isAutoWebp && cacheStats.size >= meta.size) {
            return serveOriginal(
              c,
              meta,
              dir,
              readAccess.disposition,
              readAccess.cacheScope,
              readAccess.cacheMaxAgeSeconds,
            );
          }
          const contentType = transformContentType(transformParams, isAutoWebp, meta);
          await storage.storeTransform(id, cacheKey, { buffer: transformed, type: contentType });
          return serveTransformBuffer(
            c,
            transformed,
            transformParams,
            isAutoWebp,
            meta,
            readAccess.disposition,
            readAccess.cacheScope,
            readAccess.cacheMaxAgeSeconds,
          );
        } finally {
          releaseTransformSlot();
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn(`[storage] Image transform failed for ${id}: ${msg}`);
        return serveOriginal(
          c,
          meta,
          dir,
          readAccess.disposition,
          readAccess.cacheScope,
          readAccess.cacheMaxAgeSeconds,
        );
      }
    }

    const cacheDir = path.join(dir, ".cache");
    const cachePath = path.join(cacheDir, cacheKey);

    try {
      await fs.access(cachePath);
      return serveCachedFile(
        c,
        cachePath,
        transformParams,
        isAutoWebp,
        meta,
        readAccess.disposition,
        readAccess.cacheScope,
        readAccess.cacheMaxAgeSeconds,
      );
    } catch {
      /* cache miss */
    }

    if ((await getCacheEntryCount(cacheDir)) >= MAX_CACHE_ENTRIES) {
      return serveOriginal(
        c,
        meta,
        dir,
        readAccess.disposition,
        readAccess.cacheScope,
        readAccess.cacheMaxAgeSeconds,
      );
    }

    try {
      await acquireTransformSlot();
      try {
        await fs.mkdir(cacheDir, { recursive: true });
        let pipeline = sharp(resolveStoredPath(meta, dir));

        if (transformParams?.w || transformParams?.h) {
          pipeline = pipeline.resize({
            width: transformParams.w || undefined,
            height: transformParams.h || undefined,
            fit: (transformParams.fit || "cover") as "cover" | "contain" | "fill" | "inside",
            withoutEnlargement: true,
          });
        } else if (isAutoWebp && config.autoMaxWidth > 0) {
          pipeline = pipeline.resize({ width: config.autoMaxWidth, withoutEnlargement: true });
        }

        const outputFormat = transformParams?.f || (isAutoWebp ? "webp" : null);
        const quality = transformParams?.q ?? DEFAULT_QUALITY;
        if (outputFormat === "webp") {
          pipeline = pipeline.webp(
            isAutoWebp
              ? { quality: config.autoQuality, effort: AUTO_WEBP_EFFORT, alphaQuality: 100 }
              : { quality, alphaQuality: 100 },
          );
        } else if (outputFormat === "avif") {
          pipeline = pipeline.avif({ quality });
        } else if (outputFormat === "jpeg" || outputFormat === "jpg") {
          pipeline = pipeline.jpeg({ quality, mozjpeg: true });
        } else if (outputFormat === "png") {
          pipeline = pipeline.png({ compressionLevel: 9 });
        }

        await pipeline.toFile(cachePath);
        const cacheStats = await recordStorageImageTransform(meteringOptions, cachePath, {
          sourceBytes: meta.size,
          format: outputFormat || meta.type.replace(/^image\//, ""),
          autoWebp: isAutoWebp,
        });
        if (isAutoWebp && cacheStats.size >= meta.size) {
          await fs.unlink(cachePath).catch(() => {});
          return serveOriginal(
            c,
            meta,
            dir,
            readAccess.disposition,
            readAccess.cacheScope,
            readAccess.cacheMaxAgeSeconds,
          );
        }
        return serveCachedFile(
          c,
          cachePath,
          transformParams,
          isAutoWebp,
          meta,
          readAccess.disposition,
          readAccess.cacheScope,
          readAccess.cacheMaxAgeSeconds,
        );
      } finally {
        releaseTransformSlot();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn(`[storage] Image transform failed for ${id}: ${msg}`);
      return serveOriginal(
        c,
        meta,
        dir,
        readAccess.disposition,
        readAccess.cacheScope,
        readAccess.cacheMaxAgeSeconds,
      );
    }
  };

  function getRouteIdentity(c: any): { id?: string; sessionId?: string | null } | null {
    const auth = typeof c.get === "function" ? c.get("auth") : null;
    const user = auth && typeof auth === "object" ? (auth as any).getUserIdentity?.() : null;
    if (!user || typeof user !== "object") return null;
    const sessionId = [user.sessionId, user.session_id, user.sid].find(
      (value) => typeof value === "string" && value.length > 0,
    );
    return { id: typeof user.id === "string" ? user.id : undefined, sessionId: sessionId ?? null };
  }

  function contentDisposition(disposition: StorageReadDisposition, name: string): string {
    const encoded = encodeURIComponent(name);
    return `${disposition}; filename="${encoded}"; filename*=UTF-8''${encoded}`;
  }

  async function serveOriginal(
    c: any,
    meta: StorageFile,
    dir: string,
    disposition: StorageReadDisposition = "inline",
    cacheScope: StorageCacheScope = "private",
    privateCacheMaxAgeSeconds?: number,
  ): Promise<Response> {
    const filePath = resolveStoredPath(meta, dir);
    const servedDisposition = resolveStorageDisposition({ meta, requested: disposition, cacheScope });
    const headers: Record<string, string> = {
      "Content-Type": meta.type,
      "Content-Disposition": contentDisposition(servedDisposition, meta.name),
      "Cache-Control": storageCacheControl(cacheScope, privateCacheMaxAgeSeconds),
    };
    if (storage.kind === "azure-blob") {
      const stored = await storage.get(meta.id);
      if (!stored) return c.json({ error: "Not found" }, 404);
      headers["Content-Length"] = String(stored.buffer.byteLength);
      return c.body(stored.buffer, 200, headers);
    }
    if (typeof globalThis.Bun !== "undefined") {
      const bunFile = Bun.file(filePath);
      return new Response(bunFile, { headers });
    }
    const file = fsSync.readFileSync(filePath);
    headers["Content-Length"] = String(file.byteLength);
    return c.body(file, 200, headers);
  }

  function serveCachedFile(
    c: any,
    cachePath: string,
    params: ImageTransformParams | null,
    isAutoWebp: boolean,
    originalMeta: StorageFile,
    disposition: StorageReadDisposition = "inline",
    cacheScope: StorageCacheScope = "private",
    privateCacheMaxAgeSeconds?: number,
  ): Response {
    const outputFormat = params?.f || (isAutoWebp ? "webp" : null);
    const contentType = outputFormat
      ? baseContentTypes[outputFormat] || `image/${outputFormat}`
      : originalMeta.type;
    const baseName = originalMeta.name.replace(/\.[^.]+$/, "");
    const ext = outputFormat || originalMeta.name.split(".").pop() || "bin";
    const suffix = params?.w || params?.h ? `_${params.w || "auto"}x${params.h || "auto"}` : "";
    const fileName = `${baseName}${suffix}.${ext}`;
    const servedDisposition = resolveStorageDisposition({
      meta: originalMeta,
      requested: disposition,
      cacheScope,
    });
    const headers: Record<string, string> = {
      "Content-Type": contentType,
      "Content-Disposition": contentDisposition(servedDisposition, fileName),
      "Cache-Control": storageCacheControl(cacheScope, privateCacheMaxAgeSeconds),
      ...(isAutoWebp ? { Vary: "Accept" } : {}),
    };
    if (typeof globalThis.Bun !== "undefined") {
      const bunFile = Bun.file(cachePath);
      return new Response(bunFile, { headers });
    }
    const file = fsSync.readFileSync(cachePath);
    headers["Content-Length"] = String(file.byteLength);
    return c.body(file, 200, headers);
  }

  function transformContentType(
    params: ImageTransformParams | null,
    isAutoWebp: boolean,
    originalMeta: StorageFile,
  ): string {
    const outputFormat = params?.f || (isAutoWebp ? "webp" : null);
    return outputFormat ? baseContentTypes[outputFormat] || `image/${outputFormat}` : originalMeta.type;
  }

  function transformFileName(
    params: ImageTransformParams | null,
    isAutoWebp: boolean,
    originalMeta: StorageFile,
  ): string {
    const outputFormat = params?.f || (isAutoWebp ? "webp" : null);
    const baseName = originalMeta.name.replace(/\.[^.]+$/, "");
    const ext = outputFormat || originalMeta.name.split(".").pop() || "bin";
    const suffix = params?.w || params?.h ? `_${params.w || "auto"}x${params.h || "auto"}` : "";
    return `${baseName}${suffix}.${ext}`;
  }

  function serveTransformBuffer(
    c: any,
    buffer: Buffer,
    params: ImageTransformParams | null,
    isAutoWebp: boolean,
    originalMeta: StorageFile,
    disposition: StorageReadDisposition = "inline",
    cacheScope: StorageCacheScope = "private",
    privateCacheMaxAgeSeconds?: number,
  ): Response {
    const servedDisposition = resolveStorageDisposition({
      meta: originalMeta,
      requested: disposition,
      cacheScope,
    });
    const headers: Record<string, string> = {
      "Content-Type": transformContentType(params, isAutoWebp, originalMeta),
      "Content-Disposition": contentDisposition(
        servedDisposition,
        transformFileName(params, isAutoWebp, originalMeta),
      ),
      "Cache-Control": storageCacheControl(cacheScope, privateCacheMaxAgeSeconds),
      "Content-Length": String(buffer.byteLength),
      ...(isAutoWebp ? { Vary: "Accept" } : {}),
    };
    return c.body(buffer, 200, headers);
  }
}

export function storageUploadRoutes(storage: StorageRuntimeAdapter) {
  return async (c: {
    req: {
      param: (key: string) => string;
      header: (name: string) => string | undefined;
      blob?: () => Promise<Blob>;
      arrayBuffer?: () => Promise<ArrayBuffer>;
    };
    json: (data: unknown, status?: number) => Response;
  }) => {
    if (!storage.receiveUpload) return c.json({ error: "Direct upload is not supported" }, 501);
    const uploadId = c.req.param("uploadId");
    if (!uploadId) return c.json({ error: "uploadId required" }, 400);
    try {
      const contentType = c.req.header("content-type") ?? null;
      const body =
        typeof c.req.blob === "function"
          ? await c.req.blob()
          : new Blob([await c.req.arrayBuffer!()], { type: contentType ?? undefined });
      await storage.receiveUpload(uploadId, { body, contentType });
      return c.json({ ok: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return c.json({ error: message }, 400);
    }
  };
}
